import { useState, useEffect, useRef } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from "recharts";

const SERVER_HOST = `kl26.iulianscoropad.org`;
const WS_URL      = `wss://${SERVER_HOST}/ws`;
const API_URL     = `https://${SERVER_HOST}`;

const C = {
  bg:        "#f7f4ef",
  card:      "#ffffff",
  section:   "#faf8f4",
  border:    "#e8e0d0",
  navy:      "#1a2744",
  navyMid:   "#2c3e6b",
  gold:      "#b8952a",
  goldLight: "#f0e0a0",
  text:      "#1e1e1e",
  muted:     "#6e6e6e",
  light:     "#a0a0a0",
  green:     "#276843",
  greenBg:   "#eaf5ee",
  red:       "#a81c1c",
  redBg:     "#fdf2f2",
  amber:     "#8a3e00",
  amberBg:   "#fff7ed",
  blue:      "#1a4fa0",
  blueBg:    "#eef4ff",
  purple:    "#5b21b6",
  purpleBg:  "#f5f0ff",
};

// ── Interpretări ───────────────────────────────────────────────────────────
function interpretLight(v) {
  if (v == null) return null;
  if (v <= 30)   return { label: "Întuneric complet",    icon: "🌑", color: C.navy,    bg: "#f0f0f6", desc: "Noapte sau cameră fără lumină" };
  if (v <= 150)  return { label: "Lumină foarte slabă",  icon: "🕯️", color: "#5c3a1e", bg: "#fdf3e8", desc: "Iluminat minimal" };
  if (v <= 500)  return { label: "Lumină de interior",   icon: "💡", color: C.amber,   bg: C.amberBg, desc: "Cameră iluminată normal" };
  if (v <= 1500) return { label: "Lumină moderată",      icon: "🌤️", color: C.blue,    bg: C.blueBg,  desc: "Zi înnorată / lângă fereastră" };
  if (v <= 3000) return { label: "Lumină puternică",     icon: "☀️", color: "#9a5000", bg: "#fff8ec", desc: "Lumina zilei, exterior" };
  return              { label: "Lumină intensă",         icon: "🔆", color: C.red,     bg: C.redBg,   desc: "Soare direct / lampă puternică" };
}

function interpretAccel(ax, ay, az) {
  if (ax == null) return null;
  const tilt = Math.sqrt(ax * ax + ay * ay);
  if (Math.abs(az - 1.0) < 0.08 && tilt < 0.1)
    return { label: "Orizontal / plat",      icon: "⬜", color: C.green,  bg: C.greenBg, desc: "Placa pe suprafață orizontală" };
  if (Math.abs(az + 1.0) < 0.08 && tilt < 0.1)
    return { label: "Răsturnat",             icon: "🔄", color: C.red,    bg: C.redBg,   desc: "Placa cu fața în jos" };
  if (Math.abs(ax) > 0.7)
    return { label: "Înclinat lateral",      icon: "↗️", color: C.amber,  bg: C.amberBg, desc: `Înclinare X: ~${Math.abs(ax * 90).toFixed(0)}°` };
  if (Math.abs(ay) > 0.7)
    return { label: "Înclinat față-spate",   icon: "↕️", color: C.amber,  bg: C.amberBg, desc: `Înclinare Y: ~${Math.abs(ay * 90).toFixed(0)}°` };
  if (tilt > 0.15)
    return { label: "Mișcare detectată",     icon: "📳", color: C.purple, bg: C.purpleBg, desc: `Vibrație / deplasare (${tilt.toFixed(2)}g)` };
  return   { label: "Înclinat moderat",      icon: "📐", color: C.blue,   bg: C.blueBg,  desc: `az = ${az?.toFixed(2)}g` };
}

function interpretMag(mx, my, mz) {
  if (mx == null) return null;
  const mag = Math.sqrt(mx * mx + my * my + mz * mz) * 0.1;
  if (mag < 200)  return { label: "Câmp magnetic normal",     icon: "🧭", color: C.green,  bg: C.greenBg, desc: `${mag.toFixed(0)} μT — câmp terestru normal` };
  if (mag < 500)  return { label: "Câmp ușor perturbat",      icon: "⚠️", color: C.amber,  bg: C.amberBg, desc: `${mag.toFixed(0)} μT — material feros în apropiere` };
  return               { label: "Perturbație magnetică",      icon: "🔴", color: C.red,    bg: C.redBg,   desc: `${mag.toFixed(0)} μT — magnet / motor detectat` };
}

// ── Badge interpretare ─────────────────────────────────────────────────────
function Badge({ interp }) {
  if (!interp) return null;
  return (
    <div style={{
      marginTop: 10, padding: "8px 12px",
      background: interp.bg, borderRadius: 8,
      border: `1px solid ${interp.color}22`,
      display: "flex", gap: 8, alignItems: "flex-start"
    }}>
      <span style={{ fontSize: 16, lineHeight: 1.4 }}>{interp.icon}</span>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: interp.color }}>{interp.label}</div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{interp.desc}</div>
      </div>
    </div>
  );
}

// ── Tooltip grafic ─────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 8, padding: "10px 14px",
      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      fontSize: 12
    }}>
      <div style={{ color: C.muted, marginBottom: 6 }}>
        {label ? new Date(label).toLocaleTimeString("ro-RO") : ""}
      </div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontWeight: 500 }}>
          {p.name}: {typeof p.value === "number" ? p.value.toFixed(3) : p.value}
        </div>
      ))}
    </div>
  );
};

// ── Secțiune grafic cu ghid ────────────────────────────────────────────────
function Section({ title, color, guide, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "22px 24px", marginBottom: 18,
      boxShadow: "0 1px 6px rgba(0,0,0,0.05)"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{
          margin: 0, fontSize: 14, fontWeight: 600,
          color: C.navy, display: "flex", alignItems: "center", gap: 8
        }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: color, display: "inline-block" }} />
          {title}
        </h3>
        <button onClick={() => setOpen(v => !v)} style={{
          background: "none", border: `1px solid ${C.border}`,
          borderRadius: 20, padding: "4px 12px", fontSize: 11,
          color: C.muted, cursor: "pointer", fontFamily: "inherit"
        }}>
          {open ? "Ascunde" : "📖 Ghid"}
        </button>
      </div>
      {open && (
        <div style={{
          background: C.section, border: `1px solid ${C.border}`,
          borderRadius: 8, padding: "12px 16px", marginBottom: 16,
          fontSize: 12, color: C.muted, lineHeight: 1.7
        }}>
          {guide}
        </div>
      )}
      {children}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [latest, setLatest]       = useState(null);
  const [history, setHistory]     = useState([]);
  const [wsConn, setWsConn]       = useState(false);
  const [kl26, setKl26]           = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [lastSeen, setLastSeen]   = useState(null);
  const ws = useRef(null);

  useEffect(() => {
    function connect() {
      ws.current = new WebSocket(WS_URL);
      ws.current.onopen  = () => setWsConn(true);
      ws.current.onclose = () => { setWsConn(false); setTimeout(connect, 2000); };
      ws.current.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.type === "connection_status") {
          setKl26(msg.connected); setStreaming(msg.streaming);
          if (msg.last_seen) setLastSeen(msg.last_seen);
        } else if (msg.type === "history") {
          setHistory(msg.data.slice(0, 30).reverse());
          setLatest(msg.data[0] || null);
        } else if (msg.type === "sensor_data") {
          setLatest(msg.data); setLastSeen(msg.data.received_at);
          setHistory(prev => [...prev, msg.data].slice(-30));
        } else if (msg.type === "history_cleared") {
          setHistory([]); setLatest(null);
        }
      };
    }
    connect();
    return () => ws.current?.close();
  }, []);

  const api = (path, method = "POST") =>
    fetch(`${API_URL}${path}`, { method }).catch(console.error);

  const fmtTime = ts => ts ? new Date(ts).toLocaleTimeString("ro-RO") : "";
  const fmtAgo  = ts => {
    if (!ts) return "";
    const s = Math.round((Date.now() - new Date(ts)) / 1000);
    return s < 60 ? `${s}s în urmă` : `${Math.round(s / 60)}m în urmă`;
  };

  const light = interpretLight(latest?.light);
  const accel = interpretAccel(latest?.ax, latest?.ay, latest?.az);
  const mag   = interpretMag(latest?.mx, latest?.my, latest?.mz);

  return (
    <div style={{ fontFamily: "Inter, sans-serif", background: C.bg, minHeight: "100vh", color: C.text }}>

      {/* ══ HEADER ═══════════════════════════════════════════════════════ */}
      <div style={{ background: C.navy, borderBottom: `3px solid ${C.gold}` }}>
        <div style={{
          maxWidth: 1200, margin: "0 auto",
          padding: "28px 40px 24px",
          display: "flex", alignItems: "center", gap: 28
        }}>

          {/* Titluri */}
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: 10, letterSpacing: 3, fontWeight: 500,
              color: C.goldLight, textTransform: "uppercase", marginBottom: 6
            }}>
              Universitatea de Vest din Timișoara &nbsp;·&nbsp; Facultatea de Informatică &nbsp;·&nbsp; Master Inginerie Software
            </div>

            <h1 style={{
              margin: "0 0 6px", lineHeight: 1.25,
              fontFamily: "Merriweather, Georgia, serif",
              fontSize: 22, fontWeight: 700, color: "#ffffff"
            }}>
              Implementarea protocolului de comunicare securizat<br />
              <span style={{ color: C.goldLight }}>MQTT cu TLS</span> între NXP KL26 și un server cloud
            </h1>

            <div style={{ display: "flex", gap: 24, marginTop: 8 }}>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>
                Coordonator: <strong style={{ color: "#c8d4e8" }}>Lect. Dr. Găianu Mihail</strong>
              </span>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>
                Absolvent: <strong style={{ color: "#c8d4e8" }}>Scoropad Iulian</strong>
              </span>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>Timișoara, 2026</span>
            </div>
          </div>

          {/* Status indicator — simplu, integrat */}
          <div style={{ flexShrink: 0, textAlign: "right" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
              <div style={{
                width: 11, height: 11, borderRadius: "50%", flexShrink: 0,
                background: kl26 ? "#4ade80" : "#f87171",
                boxShadow: kl26 ? "0 0 10px #4ade80aa" : "0 0 10px #f87171aa"
              }} />
              <span style={{
                fontSize: 15, fontWeight: 600,
                color: kl26 ? "#4ade80" : "#f87171"
              }}>
                {kl26 ? "KL26 Online" : "KL26 Offline"}
              </span>
            </div>
            {lastSeen && (
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                {fmtAgo(lastSeen)}
              </div>
            )}
            {streaming && (
              <div style={{ fontSize: 11, color: "#a78bfa", marginTop: 3 }}>
                ● streaming activ
              </div>
            )}
          </div>
        </div>

        {/* ── Bara butoane ─────────────────────────────────────────────── */}
        <div style={{
          background: "rgba(0,0,0,0.25)",
          padding: "14px 40px",
          display: "flex", alignItems: "center", gap: 12,
          maxWidth: 1200, margin: "0 auto",
          boxSizing: "border-box", width: "100%"
        }}>
          {/* Start */}
          <button
            onClick={() => api("/api/start")}
            disabled={!kl26 || streaming}
            style={{
              padding: "12px 28px", fontSize: 14, fontWeight: 600,
              borderRadius: 10, border: "none", cursor: (!kl26 || streaming) ? "not-allowed" : "pointer",
              background: (!kl26 || streaming) ? "#2a3a2a" : "#166534",
              color: (!kl26 || streaming) ? "#4a7a4a" : "#dcfce7",
              fontFamily: "inherit", letterSpacing: 0.3,
              boxShadow: (!kl26 || streaming) ? "none" : "0 2px 12px rgba(22,101,52,0.5)",
              transition: "all 0.2s"
            }}
          >
            ▶ &nbsp;Start colectare
          </button>

          {/* Stop */}
          <button
            onClick={() => api("/api/stop")}
            disabled={!kl26 || !streaming}
            style={{
              padding: "12px 28px", fontSize: 14, fontWeight: 600,
              borderRadius: 10, border: "none", cursor: (!kl26 || !streaming) ? "not-allowed" : "pointer",
              background: (!kl26 || !streaming) ? "#3a2a1a" : "#92400e",
              color: (!kl26 || !streaming) ? "#7a5a3a" : "#fef3c7",
              fontFamily: "inherit", letterSpacing: 0.3,
              boxShadow: (!kl26 || !streaming) ? "none" : "0 2px 12px rgba(146,64,14,0.5)",
              transition: "all 0.2s"
            }}
          >
            ■ &nbsp;Stop colectare
          </button>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Șterge date — colț dreapta */}
          <button
            onClick={() => { if (window.confirm("Ștergi tot istoricul de date?")) api("/api/data", "DELETE"); }}
            disabled={!kl26}
            style={{
              padding: "12px 22px", fontSize: 13, fontWeight: 500,
              borderRadius: 10, border: `1px solid ${kl26 ? "#7a1c1c" : "#4a3030"}`,
              cursor: !kl26 ? "not-allowed" : "pointer",
              background: "transparent",
              color: !kl26 ? "#5a3838" : "#fca5a5",
              fontFamily: "inherit",
              transition: "all 0.2s"
            }}
          >
            🗑 &nbsp;Șterge date
          </button>
        </div>
      </div>

      {/* ══ CONȚINUT ══════════════════════════════════════════════════════ */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "28px 40px" }}>

        {/* Offline */}
        {!kl26 ? (
          <div style={{
            background: C.card, border: `2px dashed ${C.border}`,
            borderRadius: 14, padding: "70px 40px", textAlign: "center"
          }}>
              <h2 style={{ color: C.muted, margin: "0 0 10px", fontWeight: 500, fontSize: 20 }}>
              Așteaptă conexiunea dispozitivului KL26...
            </h2>
            <p style={{ color: C.light, margin: 0, fontSize: 14 }}>
              Asigură-te că placa FRDM-KL26Z este pornită și conectată la rețea.
            </p>
            {lastSeen && (
              <p style={{ color: C.light, fontSize: 13, marginTop: 16 }}>
                Ultima conexiune: <strong style={{ color: C.muted }}>{new Date(lastSeen).toLocaleString("ro-RO")}</strong>
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Prompt start */}
            {!streaming && !latest && (
              <div style={{
                background: C.card, border: `1px solid ${C.border}`,
                borderRadius: 12, padding: "24px 28px", marginBottom: 20,
                textAlign: "center", color: C.muted, fontSize: 14
              }}>
                Dispozitiv conectat — apasă <strong style={{ color: C.green }}>▶ Start colectare</strong> pentru a începe recepția datelor
              </div>
            )}

            {/* Cards */}
            {latest && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18, marginBottom: 22 }}>

                {/* Lumină */}
                <div style={{
                  background: C.card, border: `1px solid ${C.border}`,
                  borderTop: `3px solid #b45309`, borderRadius: 12, padding: 20,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.light, textTransform: "uppercase", letterSpacing: 1 }}>
                    Senzor Lumină
                  </div>
                  <div style={{ fontSize: 36, fontWeight: 700, color: "#b45309", margin: "8px 0 4px" }}>
                    {latest.light}
                    <span style={{ fontSize: 14, fontWeight: 400, color: C.muted, marginLeft: 6 }}>lux</span>
                  </div>
                  {/* Bar */}
                  <div style={{ height: 6, background: C.border, borderRadius: 3, marginBottom: 4, overflow: "hidden" }}>
                    <div style={{
                      height: "100%", borderRadius: 3,
                      width: `${Math.min(100, (latest.light / 4095) * 100)}%`,
                      background: "linear-gradient(to right, #1a2744, #b8952a, #fb923c)"
                    }} />
                  </div>
                  <Badge interp={light} />
                </div>

                {/* Accelerometru */}
                <div style={{
                  background: C.card, border: `1px solid ${C.border}`,
                  borderTop: `3px solid ${C.navyMid}`, borderRadius: 12, padding: 20,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.light, textTransform: "uppercase", letterSpacing: 1 }}>
                    Accelerometru
                  </div>
                  <div style={{ fontSize: 36, fontWeight: 700, color: C.navyMid, margin: "8px 0 4px" }}>
                    {latest.az?.toFixed(2)}
                    <span style={{ fontSize: 14, fontWeight: 400, color: C.muted, marginLeft: 6 }}>g (az)</span>
                  </div>
                  <div style={{ display: "flex", gap: 14, fontSize: 12, color: C.muted }}>
                    <span>ax <strong style={{ color: C.text }}>{latest.ax?.toFixed(3)}</strong></span>
                    <span>ay <strong style={{ color: C.text }}>{latest.ay?.toFixed(3)}</strong></span>
                    <span>az <strong style={{ color: C.text }}>{latest.az?.toFixed(3)}</strong></span>
                  </div>
                  <Badge interp={accel} />
                </div>

                {/* Magnetometru */}
                <div style={{
                  background: C.card, border: `1px solid ${C.border}`,
                  borderTop: `3px solid ${C.purple}`, borderRadius: 12, padding: 20,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.light, textTransform: "uppercase", letterSpacing: 1 }}>
                    Magnetometru
                  </div>
                  <div style={{ fontSize: 36, fontWeight: 700, color: C.purple, margin: "8px 0 4px" }}>
                    {(Math.sqrt((latest.mx||0)**2+(latest.my||0)**2+(latest.mz||0)**2)*0.1).toFixed(0)}
                    <span style={{ fontSize: 14, fontWeight: 400, color: C.muted, marginLeft: 6 }}>μT</span>
                  </div>
                  <div style={{ display: "flex", gap: 14, fontSize: 12, color: C.muted }}>
                    <span>mx <strong style={{ color: C.text }}>{latest.mx}</strong></span>
                    <span>my <strong style={{ color: C.text }}>{latest.my}</strong></span>
                    <span>mz <strong style={{ color: C.text }}>{latest.mz}</strong></span>
                  </div>
                  <Badge interp={mag} />
                </div>
              </div>
            )}

            {/* Grafice */}
            {history.length > 0 && (
              <>
                <Section title="Senzor Lumină (lux)" color="#b45309" guide={
                  <><strong>Cum citești graficul:</strong> Axa Y arată intensitatea luminii (0–4095, ADC 12-bit).
                  <br />• <strong>0–30</strong>: întuneric complet &nbsp;·&nbsp; <strong>30–500</strong>: interior &nbsp;·&nbsp; <strong>500–1500</strong>: lumină moderată &nbsp;·&nbsp; <strong>1500+</strong>: exterior / soare
                  <br />Variațiile bruște pot indica umbra unei mâini, comutarea luminii sau modificări ale mediului.</>
                }>
                  <ResponsiveContainer width="100%" height={190}>
                    <LineChart data={history}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                      <XAxis dataKey="received_at" tickFormatter={fmtTime} stroke={C.light} tick={{ fontSize: 11, fill: C.muted }} />
                      <YAxis stroke={C.light} tick={{ fontSize: 11, fill: C.muted }} domain={[0, 4095]} />
                      <Tooltip content={<ChartTooltip />} />
                      <ReferenceLine y={500}  stroke="#e0c88a" strokeDasharray="4 4" label={{ value: "interior", fill: C.light, fontSize: 10 }} />
                      <ReferenceLine y={1500} stroke="#f0a860" strokeDasharray="4 4" label={{ value: "exterior", fill: C.light, fontSize: 10 }} />
                      <Line type="monotone" dataKey="light" stroke="#b45309" dot={false} strokeWidth={2} name="Lumină" />
                    </LineChart>
                  </ResponsiveContainer>
                </Section>

                <Section title="Accelerometru (g)" color={C.navyMid} guide={
                  <><strong>Cum citești graficul:</strong> Valorile sunt în g (1g = 9.81 m/s²).
                  <br />• <strong>az ≈ +1.0g</strong>: placa orizontală (gravitație normală) &nbsp;·&nbsp; <strong>az ≈ −1.0g</strong>: placa răsturnată
                  <br />• <strong>ax, ay ≠ 0</strong>: înclinare pe axele X sau Y
                  <br />Variații rapide pe toate axele = vibrație sau impact fizic.</>
                }>
                  <ResponsiveContainer width="100%" height={190}>
                    <LineChart data={history}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                      <XAxis dataKey="received_at" tickFormatter={fmtTime} stroke={C.light} tick={{ fontSize: 11, fill: C.muted }} />
                      <YAxis stroke={C.light} tick={{ fontSize: 11, fill: C.muted }} domain={[-1.5, 1.5]} />
                      <Tooltip content={<ChartTooltip />} />
                      <ReferenceLine y={1}  stroke={C.border} strokeDasharray="4 4" label={{ value: "+1g", fill: C.light, fontSize: 10 }} />
                      <ReferenceLine y={-1} stroke={C.border} strokeDasharray="4 4" label={{ value: "-1g", fill: C.light, fontSize: 10 }} />
                      <Line type="monotone" dataKey="ax" stroke="#2c3e6b" dot={false} strokeWidth={2} name="ax" />
                      <Line type="monotone" dataKey="ay" stroke="#b8952a" dot={false} strokeWidth={2} name="ay" />
                      <Line type="monotone" dataKey="az" stroke="#276843" dot={false} strokeWidth={2} name="az" />
                      <Legend wrapperStyle={{ fontSize: 12, color: C.muted }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Section>

                <Section title="Magnetometru (μT)" color={C.purple} guide={
                  <><strong>Cum citești graficul:</strong> Valorile brute pe 3 axe (FXOS8700CQ, 0.1 μT/LSB).
                  <br />• Câmpul terestru normal: ~25–65 μT total (valori stabile)
                  <br />• Variații bruște = magnet, motor electric sau obiect feros în apropiere
                  <br />• Raportul mx/my indică orientarea față de Nordul magnetic.</>
                }>
                  <ResponsiveContainer width="100%" height={190}>
                    <LineChart data={history}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                      <XAxis dataKey="received_at" tickFormatter={fmtTime} stroke={C.light} tick={{ fontSize: 11, fill: C.muted }} />
                      <YAxis stroke={C.light} tick={{ fontSize: 11, fill: C.muted }} />
                      <Tooltip content={<ChartTooltip />} />
                      <Line type="monotone" dataKey="mx" stroke={C.purple}  dot={false} strokeWidth={2} name="mx" />
                      <Line type="monotone" dataKey="my" stroke="#b45309"   dot={false} strokeWidth={2} name="my" />
                      <Line type="monotone" dataKey="mz" stroke={C.navyMid} dot={false} strokeWidth={2} name="mz" />
                      <Legend wrapperStyle={{ fontSize: 12, color: C.muted }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Section>

                {/* Ultimul pachet */}
                {latest && (
                  <div style={{
                    background: C.card, border: `1px solid ${C.border}`,
                    borderRadius: 12, padding: "20px 24px",
                    boxShadow: "0 1px 6px rgba(0,0,0,0.04)"
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>
                      Ultimul pachet MQTT
                    </div>
                    <pre style={{
                      margin: 0, fontSize: 12, color: C.green,
                      background: C.section, border: `1px solid ${C.border}`,
                      borderRadius: 8, padding: "12px 16px",
                      overflowX: "auto", fontFamily: "monospace", lineHeight: 1.6
                    }}>
                      {JSON.stringify(latest, null, 2)}
                    </pre>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* ══ FOOTER ════════════════════════════════════════════════════════ */}
      <footer style={{
        borderTop: `1px solid ${C.border}`, marginTop: 20,
        padding: "16px 40px", background: C.card,
        display: "flex", justifyContent: "space-between", alignItems: "center"
      }}>
        <span style={{ fontSize: 11, color: C.light }}>
          Universitatea de Vest din Timișoara &nbsp;·&nbsp; Facultatea de Informatică &nbsp;·&nbsp; 2026
        </span>
        <span style={{ fontSize: 11, color: C.light }}>
          Scoropad Iulian &nbsp;·&nbsp; MQTT cu TLS pe NXP FRDM-KL26Z
        </span>
      </footer>
    </div>
  );
}
